import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { requireLiveSession } from "@/lib/requireLiveSession";
import { getCachedUserRole, setCachedUserRole } from "@/lib/navCache";

export type AppRole = "admin" | "agent" | null;

/**
 * One read of the caller's platform role, telling a failed read apart from
 * "no role". The hook below caches it; the claim page's approval hand-off
 * awaits it directly so it can refuse to navigate on a failure.
 */
export async function loadUserRole(userId: string): Promise<{ ok: true; role: AppRole } | { ok: false }> {
  try {
    const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", userId).maybeSingle();
    if (error) {
      console.error("Error fetching user role:", error);
      return { ok: false };
    }
    return { ok: true, role: (data?.role as AppRole) ?? null };
  } catch (err) {
    console.error("Error fetching user role:", err);
    return { ok: false };
  }
}

export const userRoleQueryKey = (userId: string | null | undefined) => ["user-role", userId] as const;

/**
 * Forget a user's cached role once they are no longer the signed-in user.
 *
 * The QueryClient outlives sign-out and the role key is the user id, so without
 * this a sign-out and sign-in in the same tab brings back the earlier session's
 * answer, still inside its `staleTime`. That is how a claimant approved as an
 * organization's first owner signed out and in and was still routed as an
 * agent (dev, 2026-10-23). Mounted once, in `ActiveOrgProvider`, which lives
 * for the whole app; it pairs with the nav cache `useAuth.signOut` clears.
 */
export const useForgetRoleOnSignOut = (userId: string | null) => {
  const queryClient = useQueryClient();
  const previous = useRef<string | null>(userId);
  useEffect(() => {
    const before = previous.current;
    previous.current = userId;
    if (before && before !== userId) {
      queryClient.removeQueries({ queryKey: userRoleQueryKey(before), exact: true });
    }
  }, [userId, queryClient]);
};

export const useUserRole = () => {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // Route the role fetch through React Query so every call site shares one
  // cache entry keyed on the user id. Concurrent mounts (a single feed load
  // mounts this hook ~14 times) collapse into one in-flight request instead of
  // each firing its own identical query.
  const {
    data: role = null,
    isLoading: roleLoading,
    isPlaceholderData,
  } = useQuery({
    queryKey: userRoleQueryKey(user?.id),
    queryFn: async (): Promise<AppRole> => {
      // Outside the read: a skipped call must not be cached as "no role" (#794).
      await requireLiveSession();
      const result = await loadUserRole(user.id);
      if (!result.ok) {
        // A failed re-read (the tab coming back into focus) keeps the role it
        // already had: throwing leaves the cached value in place, where
        // answering null would route a signed-in admin as nobody.
        if (queryClient.getQueryData(userRoleQueryKey(user.id)) !== undefined) {
          throw new Error("role_read_failed");
        }
        // A failed first read is "no role", as it always was.
        return null;
      }
      // Keep the synchronous nav cache warm for instant first paints elsewhere.
      if (result.role === "admin" || result.role === "agent") setCachedUserRole(result.role);
      return result.role;
    },
    // Only run once auth has resolved and we actually have a user.
    enabled: !authLoading && !!user,
    // Don't refetch on remounts or route changes. A reload, a different
    // signed-in user, or signing in again (useForgetRoleOnSignOut) reads it
    // afresh.
    staleTime: 1000 * 60 * 60, // 1 hour
    // The role can change outside this tab (a founder approving a claim), so
    // re-read it whenever the tab comes back into focus, whatever its age.
    // Every mounted observer shares the one request.
    refetchOnWindowFocus: "always",
    // Seed the instant first paint from the nav cache. placeholderData (not
    // initialData) renders the cached value immediately while the hook still
    // performs its single fetch to confirm. Fall back to undefined when there
    // is no cache so a genuine first fetch still reports loading. The cached
    // value is for painting only: `isLoading` below stays true while it is
    // shown, so route gates wait for the server's answer.
    placeholderData: getCachedUserRole() ?? undefined,
  });

  const isAdmin = role === "admin";
  const isAgent = role === "agent";
  const dashboardPath = isAdmin ? "/admin" : "/feed";

  // React Query counts a placeholder as loaded, so `roleLoading` is false while
  // the nav-cache role from an earlier visit is on screen. That value can be
  // out of date: a claimant approved as an organization's owner opens the
  // approval email's /admin link in a fresh tab while the cache still says
  // "agent", and ProtectedRoute, deciding on it, sent them to /feed whenever
  // the org lookup finished before the role read (#1161 review). So the role
  // counts as loading until the server has answered for this user.
  const showingCachedRole = !!user && isPlaceholderData;

  return {
    role,
    // Loading until auth resolves and the server has answered for this user;
    // not loading when there is no user. roleLoading (React Query's derived
    // isLoading) is false for a disabled/idle query, which preserves the
    // original semantics. A cached role on screen still counts as loading:
    // route gates and onboarding decisions must not act on it.
    isLoading: authLoading || roleLoading || showingCachedRole,
    // For painting only: loading with not even a cached role to show. Nav
    // chrome that already paints from the cache uses this so it does not hide
    // while the server confirms; nothing that grants or refuses access may.
    isPaintLoading: authLoading || roleLoading,
    isAdmin,
    isAgent,
    dashboardPath,
  };
};
